import React, { useEffect, useMemo, useState } from "react";
import { FileInput, Modal, SegmentedControl, Select, Stack, Text, Textarea } from "@mantine/core";
import { Button, spacePx } from "@xplatform/design-system";
import { IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ImportDraftBody, ImporterSpec, ImportMode } from "@models/mappingConfig";
import { fileMatchesAccept } from "@utils/mappingConfig";
import { ApiErrorAlert } from "./ApiErrorAlert";

export interface ImportDialogProps {
  opened: boolean;
  importers: ImporterSpec[];
  onClose: () => void;
  /** Uploads; resolves when the draft exists (the caller navigates), rejects with the API error. */
  onSubmit: (body: Omit<ImportDraftBody, "base_version_id">) => Promise<void>;
}

/**
 * Builds a draft from a file. Importers, their target field, accepted file
 * types and modes all come from the service schema; nothing is service-specific.
 */
export const ImportDialog: React.FC<ImportDialogProps> = ({ opened, importers, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [importerId, setImporterId] = useState<string | null>(importers[0]?.id ?? null);
  const [mode, setMode] = useState<ImportMode>("merge");
  const [comment, setComment] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const importer = useMemo(() => importers.find((i) => i.id === importerId) ?? null, [importers, importerId]);

  useEffect(() => {
    if (!opened) return;
    setImporterId(importers[0]?.id ?? null);
    setComment("");
    setFile(null);
    setError(null);
  }, [opened, importers]);

  useEffect(() => {
    if (importer && !importer.modes.includes(mode)) setMode(importer.modes[0] ?? "merge");
  }, [importer, mode]);

  const wrongType = file !== null && importer !== null && !fileMatchesAccept(file, importer.accept);

  const submit = async () => {
    if (!importer || !file) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ file, importer: importer.id, mode, comment: comment.trim() });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("mappingConfig.import.title")} size="lg"
           portalProps={{ target: "#admin-portal-root" }}>
      <Stack gap="md">
        {importers.length === 0 ? (
          <Text size="sm" c="dimmed">{t("mappingConfig.import.noImporters")}</Text>
        ) : (
          <>
            <Select
              label={t("mappingConfig.import.importer")}
              data={importers.map((i) => ({ value: i.id, label: `${i.label} (${i.field})` }))}
              value={importerId}
              onChange={setImporterId}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true, portalProps: { target: "#admin-portal-root" } }}
              required
            />
            {importer && importer.modes.length > 0 && (
              <Stack gap={spacePx[1]}>
                <Text size="sm" fw={500}>{t("mappingConfig.import.mode")}</Text>
                <SegmentedControl
                  value={mode}
                  onChange={(v) => setMode(v as ImportMode)}
                  data={importer.modes.map((m) => ({ value: m, label: t(`mappingConfig.import.modes.${m}`, { defaultValue: m }) }))}
                />
                <Text size="xs" c="dimmed">{t(`mappingConfig.import.modeHint.${mode}`, { defaultValue: "" })}</Text>
              </Stack>
            )}
            <FileInput
              label={t("mappingConfig.import.file")}
              placeholder={t("mappingConfig.import.filePlaceholder")}
              accept={importer?.accept.join(",")}
              value={file}
              onChange={setFile}
              leftSection={<IconUpload size={14} />}
              description={importer?.accept.length ? t("mappingConfig.import.accepts", { types: importer.accept.join(", ") }) : undefined}
              error={wrongType ? t("mappingConfig.import.wrongType", { types: importer?.accept.join(", ") }) : undefined}
              clearable
              required
            />
            <Textarea
              label={t("mappingConfig.comment")}
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
              autosize
              minRows={2}
              maxLength={2000}
            />
          </>
        )}
        {error !== null && <ApiErrorAlert error={error} title={t("mappingConfig.import.failed")} />}
        <Stack align="flex-end">
          <Button onClick={submit} loading={busy} disabled={!importer || !file || wrongType}
                  leftSection={<IconUpload size={16} />}>
            {t("mappingConfig.import.submit")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
